export const WHATSAPP_NUMBER = '17866035862'

export type ProfileType = 'temprana' | 'intermedia' | 'madura'

export interface Option {
  text: string
  value: number
}

export interface Step {
  id: number
  title: string
  body: React.ReactNode[]
  evaluationQuestion: string
  options: Option[]
}

export const steps: Step[] = [
  {
    id: 1,
    title: 'Antes de pensar en herramientas, define el proceso',
    body: [
      'El primer error es buscar una herramienta antes de entender qué proceso quieres mejorar.',
      "Cuando un dueño me dice 'estoy pensando en comprar un CRM', la primera pregunta que le hago no es cuál CRM, ni cuánto cuesta, ni cuáles features tiene. La pregunta es: ¿cuál es exactamente el proceso que ese CRM va a mejorar?",
      "La mayoría no tiene una respuesta clara. Tienen una intuición — 'queremos organizar mejor los clientes', 'necesitamos seguimiento más efectivo' — pero no un proceso definido. Y un proceso indefinido no se mejora con tecnología. Se confirma como indefinido, ahora con un CRM más caro.",
      'Antes de mirar herramientas, escribe en una hoja:',
      '¿Qué proceso específico estoy intentando mejorar?\n¿Cómo se hace ese proceso hoy?\n¿Quién lo hace? ¿Cuánto tiempo toma?\n¿Qué parte específica genera fricción?',
      'Si no puedes responder esas cuatro preguntas con claridad, no estás listo para elegir tecnología. Estás listo para mapear tu operación.',
    ],
    evaluationQuestion:
      '¿Tienes claro hoy qué proceso específico quieres mejorar antes de elegir cualquier herramienta?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 2,
    title: 'Define el problema específico que quieres resolver',
    body: [
      'Una vez que tienes el proceso claro, viene la segunda capa: ¿qué problema dentro de ese proceso es el que vas a resolver con tecnología?',
      "Hay una diferencia importante entre 'quiero mejorar el proceso comercial' y 'quiero que cada lead nuevo reciba una respuesta en menos de 5 minutos sin que mi equipo tenga que estar pendiente'. La primera es una intención. La segunda es un problema solucionable.",
      'La tecnología solo resuelve problemas específicos. Cuando el problema está vagamente definido, la tecnología que elijas también va a resolver vagamente. Y vas a terminar con una herramienta que hace muchas cosas, ninguna bien para tu caso.',
      'El truco está en hacer la pregunta hasta volverla concreta: ¿qué exactamente quiero que pase, que hoy no pasa? ¿Y por qué no está pasando hoy?',
      'Cuando tengas esa respuesta — específica, medible, observable — entonces la herramienta correcta empieza a hacerse obvia.',
    ],
    evaluationQuestion:
      '¿Puedes describir en una frase concreta el problema específico que quieres que tu próxima herramienta resuelva?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 3,
    title: 'Estructura primero, tecnología después',
    body: [
      'Este es el punto donde la mayoría se resiste. Pero es el más importante.',
      'Si el proceso que quieres tecnologizar todavía no está estructurado — es decir, si no hay procedimiento claro, criterios definidos, roles asignados — la tecnología no va a estructurarlo por ti. Va a automatizar el desorden actual y hacerlo más rápido.',
      'La regla es simple: la tecnología no diseña la operación. La opera.',
      'Si tu proceso de ventas hoy depende de que tú te acuerdes de hacer seguimiento a cada lead, automatizar eso con un CRM no va a resolver el problema de fondo. Va a recordártelo automáticamente — pero la decisión de qué hacer con cada lead seguirá pasando por ti.',
      'Si quieres tecnología que libere, primero tienes que diseñar la operación que quieres que esa tecnología sostenga. Eso significa:\n\nProcesos documentados (no en tu cabeza)\nCriterios de decisión definidos\nRoles claros sobre quién hace qué\nMétricas que digan si está funcionando',
      'Una vez eso existe, la tecnología la multiplica. Antes, la satura.',
    ],
    evaluationQuestion:
      '¿El proceso que quieres tecnologizar tiene estructura clara hoy — procedimientos, criterios y roles definidos?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 4,
    title: 'Los 3 criterios para elegir la herramienta correcta',
    body: [
      'Cuando ya tienes proceso claro, problema definido y estructura básica, recién ahí toca elegir herramienta. Y aquí entran 3 criterios — no más, no menos.',
      '**Criterio 1: ¿Resuelve el problema específico que definiste?** La mayoría de las herramientas hacen muchas cosas. Pero la pregunta no es cuántas cosas hace, sino si resuelve la cosa específica que tú necesitas. Una herramienta que hace 50 cosas y resuelve mal la tuya es peor que una que hace 5 cosas y resuelve la tuya perfectamente.',
      '**Criterio 2: ¿La puede usar tu equipo sin volverse experto?** Si la herramienta requiere un especialista para operarla, agregaste un punto único de falla a tu negocio. Las mejores herramientas son las que tu equipo actual puede aprender en días, no en meses. Si requiere certificación, contratar a alguien nuevo, o tres semanas de capacitación, probablemente no es la correcta para tu etapa.',
      '**Criterio 3: ¿Es escalable o te encierra?** Algunas herramientas son fáciles de empezar pero difíciles de salir. Te capturan los datos en formatos cerrados, no se integran con nada más, y cuando creces te toca empezar de cero con otra herramienta. Pregúntate: si en dos años necesito cambiar, ¿cuán fácil es llevarme mis datos?',
      'Si una herramienta cumple los 3 criterios, es candidata. Si falla en uno, busca otra. No hagas concesiones aquí — son los criterios que vas a vivir cuando ya esté implementada.',
    ],
    evaluationQuestion:
      'Las herramientas que tienes hoy implementadas, ¿cumplen los 3 criterios (resuelven problema específico, equipo las usa sin ser experto, son escalables)?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 5,
    title: 'Implementa con criterio, no de golpe',
    body: [
      'Conseguiste la herramienta. Ahora viene el error final: querer implementar todo el primer mes.',
      'La implementación correcta es por etapas. Empiezas con el caso de uso más crítico — el problema específico del Paso 2 — y resuelves ese primero. No el más fácil. No el más cool. El más crítico.',
      'Cuando ese caso está funcionando bien — significa que la herramienta resuelve el problema, tu equipo la usa con autonomía, y los resultados son medibles — entonces puedes pasar al siguiente caso de uso.',
      'La razón es simple: la tecnología bien implementada en un proceso transforma ese proceso. Implementar 5 procesos a la vez con una herramienta nueva genera 5 implementaciones a medias, y al cabo de 3 meses todos los procesos siguen mal pero ahora con tecnología encima.',
      'Mejor un proceso completamente automatizado y funcionando, que cinco procesos a medio automatizar.',
    ],
    evaluationQuestion:
      'Cuando implementas una herramienta nueva, ¿la dominas completamente en un caso de uso antes de extenderla a otros procesos?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 6,
    title: 'Mide si realmente está liberando',
    body: [
      'Aquí está la pregunta que casi nadie se hace: ¿la herramienta está cumpliendo la promesa por la que la compraste?',
      'Cuando elegiste la tecnología, era para resolver un problema específico (Paso 2). Tres meses después, la pregunta es: ¿ese problema está resuelto?',
      "No '¿la herramienta está siendo usada?'. No '¿el equipo aprendió a usarla?'. La pregunta es: ¿el problema original que querías resolver, está resuelto?",
      'Si la respuesta es sí, perfecto. Pasaste la prueba. Sigues con el siguiente proceso.',
      'Si la respuesta es no, necesitas auditar honestamente:\n¿La herramienta no era la correcta?\n¿O la implementación fue incorrecta?\n¿O el problema estaba mal definido desde el principio?',
      'Las tres son posibilidades reales. Y todas se corrigen, pero solo si te haces la pregunta. Si nunca mides, la herramienta se queda implementada para siempre — funcionando a medias, costando todos los meses, sin liberar a nadie.',
    ],
    evaluationQuestion:
      '¿Mides regularmente si las herramientas que pagas están resolviendo realmente los problemas para los que las compraste?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
  {
    id: 7,
    title: 'El error más común — y cómo evitarlo',
    body: [
      'Después de acompañar a docenas de dueños en este camino, el error que se repite con más frecuencia tiene un nombre: implementar tecnología para sentirse productivo, no para producir resultados.',
      'Comprar una herramienta nueva da la sensación de que el negocio está avanzando. Hay algo nuevo, hay configuración que hacer, hay una decisión que se tomó. Pero esa sensación de progreso no equivale a progreso real.',
      'El progreso real se mide así: ¿qué tarea, decisión o proceso que antes dependía de mí, ahora ya no depende de mí?',
      'Si después de implementar tecnología nueva, sigues siendo el cuello de botella en los mismos procesos, no implementaste tecnología que libera. Implementaste tecnología que distrae.',
      'La forma de evitar este error es radical y simple: antes de comprar cualquier herramienta nueva, pregúntate qué responsabilidad concreta vas a soltar de tu mesa cuando esté funcionando. Si no puedes nombrar la responsabilidad específica, no compres la herramienta. Estás comprando la sensación, no la libertad.',
    ],
    evaluationQuestion:
      'Antes de comprar tu última herramienta, ¿pudiste nombrar específicamente qué responsabilidad concreta ibas a soltar de tu mesa?',
    options: [
      { text: 'Sí', value: 2 },
      { text: 'Parcial', value: 1 },
      { text: 'No', value: 0 },
    ],
  },
]

export const profiles = {
  temprana: {
    name: 'Implementación temprana',
    analysis: [
      'Tu respuesta a las 7 preguntas indica que la tecnología que tienes hoy probablemente no está construida sobre los cimientos correctos.',
      'Eso no significa que esté toda mal. Significa que la inversión que has hecho — en tiempo, dinero y energía — no está rindiendo lo que podría. Y mientras más tecnología agregues sin resolver los pasos previos, más vas a sentir que las herramientas complican en lugar de liberar.',
      'La buena noticia es que este es el momento ideal para corregir el rumbo. Antes de comprar la próxima herramienta, hay decisiones más importantes que tomar — y todas son resolubles.',
    ],
    whatsappMsg:
      "Hola Oscar, hice tu guía de tecnología y salí en 'Implementación temprana'. Quiero revisar mi caso específico contigo.",
  },
  intermedia: {
    name: 'Implementación intermedia',
    analysis: [
      'Tu respuesta a las 7 preguntas indica que tienes parte del trabajo resuelto, pero hay pasos específicos donde la tecnología no está rindiendo lo que debería.',
      'Este es el punto donde la mayoría se estanca. Las cosas funcionan a medias — algunas herramientas sí liberan, otras no — y es difícil identificar exactamente qué corregir sin caer en la tentación de comprar más tecnología nueva.',
      'El siguiente nivel no requiere más herramientas. Requiere ajustar los pasos donde hoy hay fricción para que la tecnología actual rinda lo que prometió cuando la elegiste.',
    ],
    whatsappMsg:
      "Hola Oscar, hice tu guía de tecnología y salí en 'Implementación intermedia'. Quiero revisar mi caso específico contigo.",
  },
  madura: {
    name: 'Implementación madura',
    analysis: [
      'Tu respuesta a las 7 preguntas indica que entiendes y aplicas la mayoría de los principios correctamente. Eso te pone en una posición que la mayoría de los dueños no alcanza.',
      'En este nivel, la pregunta ya no es si la tecnología te está liberando — es si está optimizada para lo que viene. El siguiente nivel del negocio probablemente requiera un stack tecnológico distinto al actual, o ajustes específicos a lo que ya tienes.',
      'El riesgo aquí es asumir que porque hoy funciona, va a seguir funcionando a cualquier escala. La conversación que tiene sentido en este punto es estratégica, no tácticas.',
    ],
    whatsappMsg:
      "Hola Oscar, hice tu guía de tecnología y salí en 'Implementación madura'. Quiero revisar mi caso específico contigo.",
  },
}

export const stepAnalysis: Record<number, string> = {
  1: 'Estás eligiendo herramientas antes de tener el proceso que quieres mejorar claramente mapeado. Esto significa que las decisiones de tecnología están ocurriendo sin la información necesaria — y por eso las herramientas no terminan resolviendo lo que esperabas. El primer movimiento aquí no es comprar nada nuevo. Es sentarte a mapear cómo funcionan hoy los 2-3 procesos más importantes del negocio.',
  2: 'Tienes proceso pero no problema. Eso te lleva a comprar herramientas amplias esperando que resuelvan algo no especificado. La tecnología necesita problemas concretos para resolver — sin eso, te quedas con suscripciones que hacen mucho pero no resuelven lo tuyo. El trabajo aquí es articular en una frase exactamente qué quieres que pase que hoy no pasa.',
  3: 'Estás intentando que la tecnología resuelva problemas estructurales. La automatización amplifica lo que existe — si lo que existe es desorden operativo, lo que vas a tener es desorden automatizado. Antes de implementar más tecnología, hay que diseñar la estructura que esa tecnología va a operar.',
  4: 'Estás eligiendo herramientas sin un filtro claro. Eso lleva a tener tecnología que hace muchas cosas mal o herramientas que requieren expertise externa para operarlas. Los 3 criterios — resuelve el problema, el equipo la maneja sin volverse experto, es escalable — no son negociables. Aplicarlos a tu próxima decisión va a cambiar el resultado.',
  5: 'Estás implementando tecnología en muchos frentes a la vez. Eso genera implementaciones a medias en todo y dominio en nada. La regla es radical: una herramienta resuelve un proceso completamente antes de extenderse a otro. Si tienes varias implementaciones abiertas hoy, cierra la más crítica primero.',
  6: "No estás midiendo si las herramientas cumplen su promesa. Eso significa que estás pagando suscripciones por sensación de progreso, no por resultados verificables. La pregunta correcta — '¿el problema original que querías resolver, está resuelto?' — aplicada a las herramientas que pagas hoy probablemente cambie qué cosas mantienes y qué cosas cortas.",
  7: 'Estás comprando tecnología por la sensación de progreso, no por la libertad concreta que va a generarte. La forma de cortar este patrón es radical: antes de la próxima compra, exige a la decisión nombrar qué responsabilidad específica vas a soltar de tu mesa. Si no puedes nombrarla, no compres.',
}
